// MIDAS — DIAG-OVERLAY-B shared constants + pure helpers for the `<comp-ref>`
// component chip → transient hotspot flash / tab blink flow.
//
// Spec: IMPLEMENTATION_TEMPLATES/ToDo/MidasChat/DIAG_OVERLAY_PARTB_COMP_REF_CHIPS_SPEC.md
//
// This module deliberately lives under `customer/midas/utils/` (the same
// NEUTRAL layer as diagramColorMap.ts) rather than under `components/v3/`.
// Both the v3 chat/dock surface AND the v2 diagram viewer need the same
// duration constant + the same page-join rule; a v2 → v3 import would be a
// layering violation, so the shared pieces sit one level up where both
// already import from.
//
// Pure — no React, no Redux, no DOM side effects (the reduced-motion probe
// reads matchMedia defensively and degrades to `false`).

/**
 * Transient component-flash lifetime, in ms (spec §4.3 — "~1-2s, one shared
 * duration constant, suggested 1600ms"). Owned here so the viewer's local
 * timer and any future caller cannot drift apart.
 */
export const COMP_FLASH_DURATION_MS = 1600;

/**
 * Transient tab-pill blink lifetime, in ms (spec §3.5 — "2-3 pulses of
 * var(--accent) ... over ~1.5s"). Slightly shorter than the in-sheet flash so
 * the strip has settled by the time a revealed sheet finishes flashing.
 */
export const COMP_FLASH_BLINK_MS = 1500;

/**
 * Parse the bot's `pages` attribute (or our own `data-comp-pages` mirror).
 *
 * Contract (spec §1): comma-separated GLOBAL overlay page numbers, ascending.
 * A single-page chip uses the same shape with one value — so this ALWAYS
 * returns a list, never a scalar. Junk entries are dropped; only finite
 * integers > 0 survive (page 0 / negatives are not real overlay pages).
 * Duplicates are collapsed so a malformed `1043,1043` can't double-arm.
 *
 * An empty result is the caller's signal that the tag is malformed and must be
 * left verbatim (spec §3.1 parsing rules).
 */
export const parseCompRefPages = (raw: string | undefined): number[] => {
  if (!raw) return [];
  const out: number[] = [];
  raw.split(",").forEach((part) => {
    const trimmed = part.trim();
    if (trimmed === "") return;
    const n = Number(trimmed);
    if (!Number.isInteger(n) || n <= 0) return;
    if (out.indexOf(n) !== -1) return;
    out.push(n);
  });
  return out;
};

/** Minimal structural shape shared by `midasPageModels.Artifact` and
 *  `chatBotModels.ChatArtifact` — both carry `type`, optional `page`, and an
 *  optional `data` payload whose `page` (when present) is authoritative.
 *
 *  `data` is typed `unknown` on purpose: ChatArtifact's `data` is a union of
 *  six concrete shapes (MachineCardArtifactData, TableArtifactData, …), and
 *  narrowing this field to `{ page?: number }` would make that union
 *  unassignable under TS's weak-type check. We read the page defensively
 *  below instead. */
export interface CompRefPageBearingArtifact {
  type?: string;
  page?: number;
  data?: unknown;
}

/**
 * The comp-ref join key: the overlay page an artifact represents, or undefined
 * when the artifact is not a diagram overlay at all.
 *
 * Per spec §3.4 a page P "has an open artifact" iff some tab has
 * `artifact.type === "diagram_overlay"` AND
 * `(artifact.data?.page ?? artifact.page) === P`. This is DELIBERATELY
 * narrower than ChatBotMessageV3's `recoverArtifactPage()` (which parses page
 * numbers out of image urls/titles for the alarm `<page-ref>` family) —
 * comp-ref only ever addresses real overlay sheets, and a url/title parse
 * could false-match an unrelated image tab.
 */
export const diagramOverlayPageOf = (
  artifact: CompRefPageBearingArtifact | undefined | null
): number | undefined => {
  if (!artifact) return undefined;
  if (artifact.type !== "diagram_overlay") return undefined;
  const dataPage = (artifact.data as { page?: number } | undefined)?.page;
  const page = typeof dataPage === "number" ? dataPage : artifact.page;
  if (typeof page !== "number" || !Number.isFinite(page) || page <= 0) {
    return undefined;
  }
  return page;
};

/**
 * `prefers-reduced-motion: reduce` probe for the SVG flash.
 *
 * CSS animations inside `.midas-v3-root` are already neutralised by the global
 * reduced-motion override in midas-v3-tokens.scss, but the hotspot flash is an
 * SVG SMIL `<animate>` element — SMIL is NOT covered by that CSS rule, so the
 * pulse-vs-static fork has to be decided in JS (spec §4.9). Defensive: any
 * environment without matchMedia (jsdom without a stub, older Safari) reports
 * "motion allowed", which is the pre-existing behaviour of every other v3
 * animation.
 */
export const prefersReducedMotion = (): boolean => {
  try {
    if (typeof window === "undefined" || !window.matchMedia) return false;
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
};
